/**
 * Permission Utilities
 * 
 * Shared utilities for displaying and formatting permissions consistently
 * across the user management components.
 * 
 * Reuses color configuration from src/lib/permissions/PermissionUtils.ts
 */

import { GetPermissionColorConfig as GetLibPermissionColorConfig, type ColorConfig } from "lib/permissions/PermissionUtils";

// Re-export ColorConfig for convenience
export type { ColorConfig };

/**
 * Permission bits for CRUD operations
 */
export const PermissionBits = {
	CREATE: 1,
	READ: 2,
	UPDATE: 4,
	DELETE: 8,
	RUN: 16,
} as const;

/**
 * Labels for permission bits
 */
export const PermissionBitLabels: Record<number, string> = {
	[PermissionBits.CREATE]: "Create",
	[PermissionBits.READ]: "Read",
	[PermissionBits.UPDATE]: "Update",
	[PermissionBits.DELETE]: "Delete",
	[PermissionBits.RUN]: "Run",
};

/**
 * Get the color configuration for a business object ID
 * Reuses the color configuration from lib/permissions/PermissionUtils.ts
 * @param objId - The business object ID (e.g., "C1", "H1", "S1")
 * @returns Color configuration object with backgroundColor and color
 */
export function GetPermissionColorConfig(objId: string) {
	return GetLibPermissionColorConfig(objId);
}

/**
 * Get the color for a business object ID to use in Chip components
 * Returns Material-UI color name for backward compatibility, but prefers custom colors
 * @param objId - The business object ID (e.g., "C1", "H1", "S1")
 * @returns Material-UI color name for the Chip component (or "default" to use custom colors)
 * @deprecated Use GetPermissionColorConfig instead for custom colors
 */
export function GetPermissionColor(objId: string): "primary" | "success" | "info" | "secondary" | "default" | "warning" | "error" {
	// Return "default" so we can use custom sx styling with the actual colors
	return "default";
}

/**
 * Get the display label for a business object ID
 * @param objId - The business object ID (e.g., "C1", "H1", "S1")
 * @param businessObjects - Array of business objects from database to lookup label
 * @returns The human-readable label
 * @throws Error if business objects are not provided or label is not found
 */
export function GetPermissionLabel(objId: string, businessObjects: Array<{ id: string; name: string }>): string {
	if (!businessObjects || businessObjects.length === 0) {
		throw new Error("Business objects not available from database. Cannot get permission label.");
	}
	
	const businessObj = businessObjects.find(bo => bo.id === objId);
	if (!businessObj) {
		throw new Error(`Business object with ID "${objId}" not found in database.`);
	}
	
	return businessObj.name;
}

